import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store, Eye, EyeOff, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import { useAppDispatch } from '../store/hooks';
import { setAuth, type AuthUser } from '../store/authSlice';
import { useToast } from '../components/Toast';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast    = useToast();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post<{ token: string; user: AuthUser }>('/api/v1/auth/login', { email, password });
      dispatch(setAuth({ token: res.data.token, user: res.data.user }));
      navigate('/', { replace: true });
    } catch (err) {
      toast.error({
        title: 'Credenciales incorrectas',
        description: err instanceof Error ? err.message : 'Error al iniciar sesión',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="bg-brand-primary p-3 rounded-2xl shadow-lg shadow-brand-glow mb-4">
            <Store className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-ink tracking-wide">Aye Otero</h1>
          <span className="text-xs text-brand-primary font-semibold tracking-widest uppercase mt-1">Panel de Control</span>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-page-border shadow-lg p-8">
          <h2 className="font-heading font-bold text-xl text-ink mb-6">Iniciar sesión</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full bg-white border border-page-border rounded-xl px-4 py-3 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-all"
                placeholder="admin@ayecalzados.com"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full bg-white border border-page-border rounded-xl px-4 py-3 pr-11 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-brand-primary/50 focus:ring-1 focus:ring-brand-glow transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-muted transition-colors"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-primary hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl text-sm transition-colors shadow-sm shadow-brand-glow"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Ingresando...
                </span>
              ) : 'Ingresar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
